/* ChromaLabel "cost of label problems" calculator: pure engine (no DOM).
   UMD: module.exports under Node, window.ChromaROI in the browser.
   Everything industry-specific lives between the CONFIG markers; the code below reads only from there. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.ChromaROI = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ---- CONFIG START ----
  /* Verified 2026-10-12; see SPEC.md sections 6 and 7. "benchmark" = industry estimate with a named source;
     "placeholder" = no source, the visitor replaces it. Keep defaults at the low end of any published range. */
  /** Builds one driver field definition. */
  const F = (label, unit, def, min, max, step, kind, sourceId) => {
    const o = { label, unit, default: def, min, max, step, kind };
    if (sourceId) o.sourceId = sourceId;
    return o;
  };
  /** Builds an industry's "basics" block; hourly cites the wage source plus the BLS benefits-share source. */
  const BASICS = (spend, wage, wageSrc, wageNote, days, daysNote) => ({
    labelSpend: { default: spend, kind: 'placeholder' },
    hourly: { default: wage, kind: 'benchmark', sourceId: wageSrc, alsoSourceIds: ['bls-ecec'], note: wageNote },
    workdays: { default: days, kind: 'placeholder', note: daysNote }
  });
  /** Builds a "labels that fail and get redone" driver. */
  const REL = (title, why, iLabel, items, fail, mins, minLabel) => ({ id: 'relabel', type: 'relabel', title, why, fields: {
    items: F(iLabel || 'Labels used per year', '#', items, 0, 5e7, 100, 'placeholder'),
    failPct: F('Share that fail and get redone (%)', '%', fail, 0, 100, 0.5, 'placeholder'),
    minutes: F(minLabel || 'Minutes to redo one label', 'min', mins, 0, 480, 1, 'placeholder') } });
  /** Builds a "time spent finding and checking" driver. */
  const SRCH = (title, why, pLabel, people, mLabel) => ({ id: 'search', type: 'search', title, why, fields: {
    people: F(pLabel, '#', people, 0, 5000, 1, 'placeholder'),
    minutesPerDay: F(mLabel || 'Minutes per person per day spent finding and checking', 'min', 5, 0, 480, 1, 'placeholder') } });
  /** Builds an "events" driver: how many x share affected x cost each. Volume key is always `volume`. */
  const EV = (id, title, why, v, r, c) => ({ id, type: 'events', title, why, fields: {
    volume: F(v[0], '#', v[1], 0, 5e7, 100, 'placeholder'),
    ratePct: F(r[0], '%', r[1], 0, 100, r[3] || 0.05, r[2] ? 'benchmark' : 'placeholder', r[2]),
    costPerEvent: F(c[0], '$', c[1], 0, 1e5, 5, c[2] ? 'benchmark' : 'placeholder', c[2]) } });
  /** Builds a "waste" driver: spend x share thrown out. */
  const WASTE = (id, title, why, sLabel, sDef, rLabel, rDef) => ({ id, type: 'waste', title, why, fields: {
    spend: F(sLabel, '$', sDef, 0, 1e8, 1000, 'placeholder'),
    ratePct: F(rLabel, '%', rDef, 0, 100, 0.1, 'placeholder') } });
  /** Builds a "downtime" driver: stops x hours x cost per hour. */
  const DOWN = (id, title, why, iLabel, iDef, hLabel, cLabel, cDef) => ({ id, type: 'downtime', title, why, fields: {
    incidents: F(iLabel, '#', iDef, 0, 1e4, 1, 'placeholder'),
    hoursEach: F(hLabel, 'hrs', 1, 0, 200, 0.5, 'placeholder'),
    costPerHour: F(cLabel, '$', cDef, 0, 1e6, 50, 'placeholder') } });
  /** Builds a source entry. */
  const S = (publisher, title, year, url, quote) => ({ publisher, title, year, url, quote });

  const SOURCES = {
    'bls-ecec': S('U.S. Bureau of Labor Statistics', 'Employer Costs for Employee Compensation, June 2026 (private industry)', '2026',
      'https://www.bls.gov/news.release/ecec.nr0.htm',
      'benefit costs averaged $14.07 and accounted for the remaining 30.0 percent'),
    'bls-ooh-cls': S('U.S. Bureau of Labor Statistics', 'Occupational Outlook Handbook: Clinical Laboratory Technologists and Technicians, 2025 median pay', '2025',
      'https://www.bls.gov/ooh/healthcare/clinical-laboratory-technologists-and-technicians.htm',
      '$30.26 per hour'),
    'onet-537062': S('O*NET OnLine (BLS 2025 wage data)', 'Laborers and Freight, Stock, and Material Movers, Hand (53-7062): U.S. median hourly wage', '2025',
      'https://www.onetonline.org/link/localwages/53-7062.00',
      '$19.35'),
    'onet-254031': S('O*NET OnLine (BLS 2025 wage data)', 'Library Technicians (25-4031): U.S. median hourly wage', '2025',
      'https://www.onetonline.org/link/localwages/25-4031.00',
      '$21.43'),
    'onet-499071': S('O*NET OnLine (BLS 2025 wage data)', 'Maintenance and Repair Workers, General (49-9071): U.S. median hourly wage', '2025',
      'https://www.onetonline.org/link/localwages/49-9071.00',
      '$23.84'),
    'wagar-2008': S('Archives of Pathology & Laboratory Medicine', 'Wagar EA et al., Specimen labeling errors: a Q-Probes analysis of 147 clinical laboratories, 132(10):1617-22', '2008',
      'https://mdanderson.elsevierpure.com/en/publications/specimen-labeling-errors-a-q-probes-analysis-of-147-clinical-labo',
      'Labeling errors were identified at a rate of 0.92 per 1000 labels.'),
    'kahn-2005': S('acutecaretesting.org', 'Kahn SE, Specimen mislabeling: a significant and costly cause of potentially serious medical errors (illustrative hospital example, 2005 dollars; the author reports USD 712 in added charges per case at his own lab)', '2005',
      'https://acutecaretesting.org/articles/specimen-mislabeling-a-significant-and-costly-cause-of-potentially-serious-medical-errors',
      'the average total cost per mislabeling incident is USD 500'),
    'tjc-npsg-2024': S('The Joint Commission', 'National Patient Safety Goals, Hospital Program (NPSG.01.01.01)', '2024',
      'https://jointcommission.org/-/media/tjc/documents/standards/national-patient-safety-goals/2024/npsg_chapter_hap_jan2024.pdf',
      'Label containers used for blood and other specimens in the presence of the patient.'),
    'warehouseos': S('WarehouseOS', 'The Cost of Picking Mistakes for Small Businesses (reporting a survey of 300 supply-chain and distribution managers)', 'n.d.',
      'https://warehouseos.com/the-cost-of-picking-mistakes-for-small-businesses/',
      'The cost of the average mis-pick is around $30 per incident.'),
    'osha-top10': S('OSHA', 'Top 10 Most Frequently Cited Standards, FY2026 preliminary (Oct 1, 2025 to Aug 31, 2026): #2 Hazard Communication (1910.1200), #3 Lockout/Tagout (1910.147)', '2026',
      'https://www.osha.gov/top10citedstandards',
      ''),
    'osha-1910-1200': S('OSHA', '29 CFR 1910.1200(f)(6), Hazard Communication: workplace labeling (exceptions in (f)(7) and (f)(8))', 'current',
      'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.1200',
      'the employer shall ensure that each container of hazardous chemicals in the workplace is labeled, tagged or marked'),
    'osha-penalties-2026': S('OSHA', '2026 Annual Adjustments to OSHA Civil Penalties (memo, May 21, 2026): serious violations up to $16,550 each', '2026',
      'https://www.osha.gov/memos/2026-05-21/2026-annual-adjustments-osha-civil-penalties',
      'There are no inflation-based increases to OSHA civil penalties for 2026, so 2025 penalty amounts remain in effect for 2026.'),
    'nixon-2009': S('Purdue University Libraries', 'Nixon JM, The Right Book on the Right Shelf: Missing and Misshelved Books', '2009',
      'https://docs.lib.purdue.edu/lib_fsdocs/1',
      'Study of the problem indicated that in fact 20% of our books were either missing or miss-shelved.'),
    'dla-guide-2025': S('Defense Logistics Agency', 'DLA Packaging and Marking Guide 2025 (MIL-STD-129 marking; use the revision your contract cites)', '2025',
      'https://www.dla.mil/Portals/104/Documents/J3LogisticOperations/Packaging/J3_DLA%20Packaging%20and%20Marking%20Guide%202025%20V2.pdf',
      ''),
    'cfr-493-1242': S('Cornell Law School Legal Information Institute', '42 CFR 493.1242', 'current',
      'https://www.law.cornell.edu/cfr/text/42/493.1242',
      '(3) Specimen labeling, including patient name or unique patient identifier and, when appropriate, specimen source.'),
    'cfr-493-1252': S('Cornell Law School Legal Information Institute', '42 CFR 493.1252', 'current',
      'https://www.law.cornell.edu/cfr/text/42/493.1252',
      '(3) Preparation and expiration dates. / must not be used when they have exceeded their expiration date, have deteriorated, or are of substandard quality.'),
    'cfr-211-80': S('Cornell Law School Legal Information Institute', '21 CFR 211.80', 'current',
      'https://www.law.cornell.edu/cfr/text/21/211.80',
      'Each lot shall be appropriately identified as to its status (i.e., quarantined, approved, or rejected).'),
    'loc-preservation-books': S('Library of Congress', 'Preservation FAQ: Books', 'current',
      'https://www.loc.gov/preservation/about/faqs/books.html',
      'Do not use self-stick tape, even if it is marketed as "archival."'),
    'dartmouth-book-repair': S('Dartmouth College Library', 'Simple Book Repair Manual', 'current',
      'https://collections.dartmouth.edu/teitexts/book-repair/diplomatic/SimpleBookRepairManual-diplomatic.html',
      'In-house book repair is appropriate for general collections materials'),
    'una-book-mending': S('University of North Alabama Collier Library', 'Book Mending for the Lending Library: Checklist', 'current',
      'https://libguides.una.edu/c.php?g=1450941',
      'Depending on the nature of the damage, it may be more cost-effective to replace a book rather than repair it.'),
    'fda-food-code-350118': S('U.S. Food and Drug Administration', 'FDA Food Code 2022, § 3-501.18(A)', '2022',
      'https://www.fda.gov/media/164194/download',
      '(1) Exceeds the temperature and time combination specified in ¶ 3-501.17(A), except time that the product is frozen; P (2) Is in a container or PACKAGE that does not bear a date or day'),
    'fda-restaurant-risk-2017-2018': S('U.S. Food and Drug Administration', 'Report on the Occurrence of Foodborne Illness Risk Factors in Fast-Food and Full-Service Restaurants 2017-2018', '2023',
      'https://www.fda.gov/media/169390/download',
      'These data items had out-of-compliance percentages of 80%, 69%, and 64%, respectively.'),
    'fda-restaurant-methodology-2017-2018': S('U.S. Food and Drug Administration', 'Report on the Occurrence of Foodborne Illness Risk Factors in Fast-Food and Full-Service Restaurants 2017-2018: methodology', '2023',
      'https://www.fda.gov/media/169390/download',
      'The 80% score should not be interpreted to mean that foods were cooled improperly 80% of the time.'),
    'refed-foodservice-2024': S('ReFED', 'Food Waste by Sector: Foodservice fact sheet (2024 data)', '2024',
      'https://refed.org/downloads/by-sector-foodservice-2025.pdf',
      'Surplus food from foodservice was valued at $157 billion dollars in 2024. / That’s equivalent to 14% of foodservice sales.'),
    'onet-352021': S('O*NET OnLine (BLS 2025 wage data)', 'Food Preparation Workers (35-2021): U.S. median hourly wage', '2025',
      'https://www.onetonline.org/link/localwages/35-2021.00',
      '$16.98')
  };

  const INDUSTRIES = {
    healthcare: {
      id: 'healthcare', name: 'Healthcare', short: 'Healthcare', examples: 'Hospitals, labs, pharmacies, clinics',
      headline: 'Label problems in your hospital, lab or pharmacy may be costing you',
      basics: BASICS(5000, 43, 'bls-ooh-cls', 'Lab technologist median $30.26/hr ÷ 0.70 (benefits are 30% of total pay).', 156, 'Three 12-hour shifts a week is about 156.'),
      drivers: [
        EV('specimen', 'Specimen labeling errors', 'A mislabeled specimen means a redraw, a delayed result and staff time to sort it out.',
          ['Specimens labeled per year', 20000],
          ['Share with a labeling error (%)', 0.092, 'wagar-2008', 0.001],
          ['Cost per mislabeled specimen', 500, 'kahn-2005']),
        WASTE('expired', 'Expired or undated supplies thrown out', 'Items with no clear date or ID get discarded to be safe, even when they are still good.',
          'Spend on supplies and meds that can expire', 50000, 'Share thrown out as expired or undated (%)', 1),
        REL('Labels that fail and get redone', 'Labels that smear, peel or freeze off have to be reprinted and reapplied by clinical staff.', null, 10000, 1, 3),
        SRCH('Time spent finding and checking supplies', 'Unclear bins and shelves cost staff a few minutes every shift.', 'Staff who look for or check supplies', 10,
          'Minutes per person per shift spent finding and checking')
      ],
      context: [
        { text: 'In a College of American Pathologists Q-Probes study of 147 labs and 3.3 million specimen labels, labeling errors ran 0.92 per 1,000 labels.', sourceId: 'wagar-2008' },
        { text: 'The Joint Commission’s hospital National Patient Safety Goals (2024) call for labeling blood and specimen containers in the presence of the patient.', sourceId: 'tjc-npsg-2024' }
      ]
    },
    manufacturing: {
      id: 'manufacturing', name: 'Manufacturing', short: 'Manufacturing', examples: 'Plants, warehouses, distribution',
      headline: 'Label problems in your plant or warehouse may be costing you',
      basics: BASICS(8000, 27, 'onet-537062', 'Material handler median $19.35/hr ÷ 0.70 (benefits are 30% of total pay).', 250),
      drivers: [
        DOWN('downtime', 'Line stops from missing or unclear labels', 'A wrong or unreadable label can stop a line or a dock until someone sorts it out.',
          'Stops per year caused by label problems', 2, 'Hours lost per stop', 'Cost of one hour of downtime', 500),
        EV('mispick', 'Picking and shipping errors', 'A label that is easy to misread sends the wrong item out the door, and you pay to fix it.',
          ['Orders shipped per year', 10000],
          ['Share picked wrong because of a label (%)', 0.5],
          ['Cost to fix one mis-pick', 30, 'warehouseos']),
        REL('Labels that fail in tough conditions', 'Labels that peel, smear or scuff off in handling, heat or cold get replaced by hand.', null, 10000, 2, 5),
        SRCH('Time spent searching for parts, bins and tools', 'Items that are hard to tell apart take time to find, sort and double-check.', 'People who look for or check items', 10),
        WASTE('scrap', 'Material scrapped because it could not be identified', 'Stock with a missing or unreadable label is often scrapped instead of risked.',
          'Spend on materials that could be scrapped', 100000, 'Share scrapped because of labeling (%)', 0.5)
      ],
      context: [
        { text: 'Hazard Communication (29 CFR 1910.1200) is #2 on OSHA’s preliminary FY2026 list of most-cited standards.', sourceId: 'osha-top10' },
        { text: 'With limited exceptions, it requires containers of hazardous chemicals in the workplace to be labeled, tagged or marked.', sourceId: 'osha-1910-1200' },
        { text: 'The maximum OSHA penalty for a serious violation is $16,550 (2026).', sourceId: 'osha-penalties-2026' }
      ]
    },
    education: {
      id: 'education', name: 'Education', short: 'Education', examples: 'Schools, libraries, universities',
      headline: 'Label problems in your school or library may be costing you',
      basics: BASICS(2000, 30, 'onet-254031', 'Library technician median $21.43/hr ÷ 0.70 (benefits are 30% of total pay at private employers; higher at public ones).', 180, 'A school year is about 180 days.'),
      drivers: [
        EV('lost', 'Books and materials lost or misplaced', 'Books, devices and kits that are hard to identify get lost, misfiled or replaced.',
          ['Items you keep track of (books, devices, equipment)', 10000],
          ['Share lost or replaced each year (%)', 1],
          ['Cost to replace one item', 20]),
        { id: 'handLabor', type: 'handLabor', title: 'Hand-labeling and processing time', why: 'Staff and volunteers spend hours writing, trimming and sticking labels by hand.',
          fields: { hoursPerWeek: F('Hours per week spent making labels by hand', 'hrs', 3, 0, 168, 0.5, 'placeholder') } },
        REL('Spine and ID labels that wear off', 'Labels that peel or wear off in daily use have to be replaced.', null, 5000, 3, 2),
        SRCH('Time spent finding misplaced items', 'Unclear labels make staff slow down to look for and reshelve things.', 'People who look for or sort items', 3,
          'Minutes per person per day spent finding and sorting')
      ],
      context: [
        { text: 'One academic library found 20% of its books were missing or misshelved before it started regular inventories.', sourceId: 'nixon-2009' }
      ]
    },
    government: {
      id: 'government', name: 'Government & military', short: 'Government', examples: 'Agencies, bases, depots, public works',
      headline: 'Label problems in your facility may be costing you',
      basics: BASICS(5000, 27, 'onet-537062', 'Material handler median $19.35/hr ÷ 0.70 (benefits are 30% of total pay at private employers; higher at public ones).', 250),
      drivers: [
        EV('rejected', 'Shipments held or rejected for marking problems', 'Missing or wrong markings can hold up a receipt until it is relabeled or sent back.',
          ['Shipments sent or received per year', 1000],
          ['Share held or rejected for marking (%)', 1, null, 0.1],
          ['Cost to rework or reship one', 250]),
        REL('Asset and ID labels that fail', 'Asset tags and ID labels that fade or peel have to be reissued and reapplied.', null, 5000, 2, 5),
        SRCH('Time spent finding and verifying items', 'Items that are hard to tell apart take time to find and double-check.', 'People who look for or verify items', 5),
        WASTE('shelfLife', 'Shelf-life items discarded because dates were unclear', 'Stock without a clear date gets thrown out to be safe.',
          'Spend on shelf-life items', 50000, 'Share discarded because the date was unclear (%)', 1)
      ],
      context: [
        { text: 'Defense shipments are marked to MIL-STD-129 (Military Marking for Shipment and Storage); DLA tells vendors to follow the revision their contract cites.', sourceId: 'dla-guide-2025' }
      ]
    },
    utilities: {
      id: 'utilities', name: 'Utilities', short: 'Utilities', examples: 'Electric, water, gas, telecom',
      headline: 'Label problems across your system may be costing you',
      basics: BASICS(5000, 34, 'onet-499071', 'Maintenance worker median $23.84/hr ÷ 0.70 (benefits are 30% of total pay).', 250),
      drivers: [
        EV('misid', 'Repeat trips from misidentified equipment', 'A faded or missing tag can send a crew to the wrong valve, meter or cable and back again.',
          ['Field work orders per year', 1000],
          ['Share needing a repeat trip because of a label (%)', 1, null, 0.1],
          ['Cost of one repeat trip', 150]),
        REL('Outdoor tags and markers that fade or fall off', 'Sun, weather and washdowns wear out tags that then have to be replaced in the field.', 'Tags and markers in service', 2000, 5, 20,
          'Field minutes to replace one tag'),
        DOWN('outage', 'Longer outages from unclear markings', 'When equipment is hard to identify, restoring service or isolating a fault takes longer.',
          'Outages or shutdowns per year made longer by labels', 2, 'Extra hours per event', 'Cost of one hour', 500),
        SRCH('Field time spent identifying equipment', 'Crews lose minutes on every job confirming what they are looking at.', 'Field staff', 6)
      ],
      context: [
        { text: 'Lockout/tagout (29 CFR 1910.147) is #3 on OSHA’s preliminary FY2026 list of most-cited standards.', sourceId: 'osha-top10' },
        { text: 'The maximum OSHA penalty for a serious violation is $16,550 (2026).', sourceId: 'osha-penalties-2026' }
      ]
    },
    lab: {
      id: 'lab', name: 'Laboratories & life sciences', short: 'Labs', examples: 'Clinical labs, biobanks, research labs, biotech QC',
      headline: 'Label problems in your lab or biobank may be costing you',
      basics: BASICS(3000, 43, 'bls-ooh-cls', 'Lab technologist median $30.26/hr ÷ 0.70 (benefits are 30% of total pay).', 250),
      drivers: [
        EV('sampleErrors', 'Sample labeling errors', 'A mislabeled sample means a recollection, a delayed result and staff time to sort it out.',
          ['Samples labeled per year', 20000],
          ['Share with a labeling error (%)', 0.092, 'wagar-2008', 0.001],
          ['Cost per mislabeled sample', 500, 'kahn-2005']),
        REL('Labels that fail in cold storage and get redone', 'A lifted or faded label means a lookup, a reprint and a reapply.',
          'Labels used in cold storage per year', 5000, 1, 10),
        SRCH('Time spent finding samples in freezers and racks', 'Unclear boxes mean doors held open and minutes lost daily.',
          'People who find samples in freezers and racks', 5),
        WASTE('reagents', 'Reagents and controls thrown out as expired or undated', 'An undated opened reagent gets discarded to be safe.',
          'Spend on reagents and controls', 25000, 'Share discarded as expired or undated (%)', 1),
        { id: 'handLabor', type: 'handLabor', title: 'Hand-writing tubes and labels', why: 'Hand-labeling is slow and hard to read.',
          fields: { hoursPerWeek: F('Hours per week spent hand-writing tubes and labels', 'hrs', 1, 0, 168, 0.5, 'placeholder') } }
      ],
      context: [
        { text: 'More than 3.3 million specimen labels were reviewed by 147 laboratories; labeling errors were identified at a rate of 0.92 per 1,000 labels.', sourceId: 'wagar-2008' },
        { text: 'CLIA requires written procedures for specimen labeling, including patient name or a unique patient identifier and, when appropriate, specimen source.', sourceId: 'cfr-493-1242' },
        { text: 'CLIA requires preparation and expiration dates for reagents, solutions, culture media, controls and other supplies, and bars their use after expiration or deterioration.', sourceId: 'cfr-493-1252' },
        { text: 'Drug manufacturers must identify each component lot as quarantined, approved or rejected.', sourceId: 'cfr-211-80' }
      ]
    },
    library: {
      id: 'library', name: 'Libraries & archives', short: 'Libraries', examples: 'Public, school and academic libraries, archives',
      headline: 'Label problems in your library or archive may be costing you',
      basics: BASICS(1500, 30, 'onet-254031', 'Library technician median $21.43/hr ÷ 0.70 (benefits are 30% of total pay at private employers; higher at public ones).', 180, 'A school year is about 180 days; year-round staff work about 250.'),
      drivers: [
        EV('missing', 'Books lost and replaced because they couldn’t be found', 'An unreadable spine leads to misshelving, and a misshelved book gets bought twice.',
          ['Circulating items', 20000],
          ['Share replaced per year (%)', 0.5],
          ['Cost to replace and process one item', 20]),
        SRCH('Shelf-reading and hunting for misshelved items', 'Hard-to-read bands slow every find and reshelve.',
          'People who shelf-read or find items', 2),
        REL('Spine labels and bands that peel, fade or get redone', 'Worn labels are redone by hand.',
          'Spine labels and bands in service', 5000, 2, 3, 'Minutes to pull, relabel, re-cover and reshelve one item'),
        { id: 'handLabor', type: 'handLabor', title: 'Hand-processing time', why: 'Imprinted bands replace hand-lettering.',
          fields: { hoursPerWeek: F('Hours per week cutting, lettering and applying bands', 'hrs', 2, 0, 168, 0.5, 'placeholder') } },
        EV('damaged', 'Damaged books replaced that an early repair could have saved', 'Simple repairs on circulating copies are usually worth doing.',
          ['Books pulled for damage per year', 200],
          ['Share replaced but repairable (%)', 10],
          ['Replacement cost minus repair cost', 20])
      ],
      context: [
        { text: 'One academic library found 20% of its books were missing or misshelved before it began regular inventories.', sourceId: 'nixon-2009' },
        { text: 'The Library of Congress advises against self-stick tape on collection items, even tape marketed as archival.', sourceId: 'loc-preservation-books' },
        { text: 'Dartmouth’s repair manual says in-house repair is appropriate for general-collections materials.', sourceId: 'dartmouth-book-repair' },
        { text: 'The University of North Alabama notes that replacement can be more cost-effective than repair, depending on the damage.', sourceId: 'una-book-mending' }
      ]
    },
    food: {
      id: 'food', name: 'Food service & food production', short: 'Food service', examples: 'Restaurants, school and hospital kitchens, bakeries, food producers',
      headline: 'Label problems in your kitchen or food operation may be costing you',
      basics: BASICS(1000, 24, 'onet-352021', 'Food preparation worker median $16.98/hr ÷ 0.70 (benefits are 30% of total pay).', 250),
      drivers: [
        WASTE('undated', 'Food thrown out because it was undated, unreadable or past its date', 'Undated ready-to-eat food must be discarded under FDA Food Code 3-501.18.',
          'Food purchases that are prepped or opened and held cold per year', 50000, 'Share discarded as undated or expired (%)', 1),
        { id: 'handLabor', type: 'handLabor', title: 'Time spent making and writing labels by hand', why: 'Tape-and-marker labels are slow and inconsistent between shifts.',
          fields: { hoursPerWeek: F('Hours per week spent making and writing labels by hand', 'hrs', 1, 0, 168, 0.5, 'placeholder') } },
        REL('Labels that won’t come off reused containers', 'Residue costs dish-room time.',
          'Labeled containers washed per year', 10000, 5, 2, 'Minutes to clean one container'),
        SRCH('Time checking dates in coolers', 'Unclear labels slow every cooler check.',
          'Staff who check or sort cooler items', 3, 'Minutes per person per day checking dates')
      ],
      context: [
        { text: 'FDA Food Code 3-501.18 calls for discarding covered ready-to-eat food that exceeds its time limit or has no date or day on the container.', sourceId: 'fda-food-code-350118' },
        { text: 'FDA observers found the date-marking item out of compliance at least once in 64% of full-service restaurants studied in 2017–2018.', sourceId: 'fda-restaurant-risk-2017-2018' },
        { text: 'That finding does not mean 64% of foods were improperly date marked; the report scores whether an item was out of compliance at least once.', sourceId: 'fda-restaurant-methodology-2017-2018' },
        { text: 'ReFED valued foodservice surplus food at $157 billion in 2024, about 14% of foodservice sales; most is not label-related, so it is not included in the calculation.', sourceId: 'refed-foodservice-2024' }
      ]
    }
  };
  // ---- CONFIG END ----
  /* Shared fields that are not industry-specific. labelSpend, hourly and workdays take their
     default and kind from each industry's `basics`; improvementPct and premiumPct are global. */
  const SHARED_FIELDS = {
    labelSpend: { label: 'What you spend on labels, tags and tape per year', unit: '$', min: 0, max: 1e7, step: 100 },
    hourly: { label: 'Fully loaded hourly labor cost', unit: '$', min: 1, max: 500, step: 1 },
    workdays: { label: 'Days (or shifts) each person works per year', unit: '#', min: 1, max: 366, step: 1 },
    improvementPct: { label: 'Share of these costs better labels could prevent (%)', unit: '%', default: 25, min: 0, max: 75, step: 5, kind: 'placeholder' },
    premiumPct: { label: 'Extra cost of upgraded labels vs. what you buy now (%)', unit: '%', default: 20, min: 0, max: 100, step: 1, kind: 'placeholder' }
  };

  /** Plain-word description of each formula type, for the sources and assumptions list. */
  const FORMULA_WORDS = {
    events: 'How many × share affected × cost each',
    relabel: 'Labels used × share that fail × minutes to redo ÷ 60 × hourly labor cost',
    search: 'People × minutes per day ÷ 60 × days worked per year × hourly labor cost',
    handLabor: 'Hours per week × (days worked per year ÷ 5) × hourly labor cost',
    waste: 'Spend × share wasted',
    downtime: 'Stops per year × hours per stop × cost per hour'
  };

  /** True when `o` has its own property `k`. */
  const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

  /** Industry config for an id; unknown ids fall back to the first industry in the config. */
  function getIndustry(id) {
    return has(INDUSTRIES, id) ? INDUSTRIES[id] : INDUSTRIES[Object.keys(INDUSTRIES)[0]];
  }

  /** Parses a number or a string like "1,200", "$50" or "2.5%"; NaN when it cannot. */
  function parseNumber(v) {
    if (typeof v === 'number') return isFinite(v) ? v : NaN;
    if (typeof v !== 'string') return NaN;
    const s = v.replace(/[,\s$%]/g, '');
    const n = s === '' ? NaN : Number(s);
    return isFinite(n) ? n : NaN;
  }

  /** Non-numbers become the field default, then the value is clamped to [min, max]. */
  function sanitize(value, def) {
    const n = parseNumber(value);
    return isNaN(n) ? def.default : Math.min(def.max, Math.max(def.min, n));
  }

  /** Flat map of input key to field definition: basics, `<driverId>.<field>`, then the two global fields. */
  function fieldDefs(industryId) {
    const ind = getIndustry(industryId);
    const out = {};
    ['labelSpend', 'hourly', 'workdays'].forEach((k) => { out[k] = Object.assign({}, SHARED_FIELDS[k], ind.basics[k]); });
    ind.drivers.forEach((d) => Object.keys(d.fields).forEach((k) => { out[d.id + '.' + k] = d.fields[k]; }));
    out.improvementPct = SHARED_FIELDS.improvementPct;
    out.premiumPct = SHARED_FIELDS.premiumPct;
    return out;
  }

  /** Flat default inputs for an industry. */
  function defaultsFor(industryId) {
    const defs = fieldDefs(industryId);
    const out = {};
    Object.keys(defs).forEach((k) => { out[k] = defs[k].default; });
    return out;
  }

  /** Ordered, de-duplicated source ids an industry uses (basics, fields, context) that exist in SOURCES. */
  function sourceIdsFor(industryId) {
    const ind = getIndustry(industryId);
    const seen = [];
    const add = (id) => { if (id && has(SOURCES, id) && seen.indexOf(id) < 0) seen.push(id); };
    const addDef = (def) => { add(def.sourceId); (def.alsoSourceIds || []).forEach(add); };
    Object.keys(ind.basics).forEach((k) => addDef(ind.basics[k]));
    ind.drivers.forEach((d) => Object.keys(d.fields).forEach((k) => addDef(d.fields[k])));
    (ind.context || []).forEach((c) => add(c.sourceId));
    return seen;
  }

  /** Volume of an `events` driver: `volume`, else the first field that is not the rate or the cost. */
  function volumeOf(a) {
    if (a.volume !== undefined) return a.volume;
    return a[Object.keys(a).filter((x) => x !== 'ratePct' && x !== 'costPerEvent')[0]];
  }

  /* One function per formula type. a = this driver's sanitized values by field name;
     c = { hourly, workdays }. Each returns annual dollars. */
  const FORMULAS = {
    /** volume x share x cost per event. */
    events: (a) => volumeOf(a) * (a.ratePct / 100) * a.costPerEvent,
    /** items x share failing x minutes to redo / 60 x hourly. */
    relabel: (a, c) => a.items * (a.failPct / 100) * (a.minutes / 60) * c.hourly,
    /** people x minutes per day x workdays / 60 x hourly. */
    search: (a, c) => (a.people * a.minutesPerDay * c.workdays / 60) * c.hourly,
    /** hours per week x (workdays / 5) x hourly. */
    handLabor: (a, c) => a.hoursPerWeek * (c.workdays / 5) * c.hourly,
    /** spend x share wasted. */
    waste: (a) => a.spend * (a.ratePct / 100),
    /** incidents x hours each x cost per hour. */
    downtime: (a) => a.incidents * a.hoursEach * a.costPerHour
  };

  /** Annual cost of label problems plus the upgrade verdict; bad inputs use defaults, unknown industries the first one. */
  function computeROI(industryId, inputs) {
    const ind = getIndustry(industryId);
    const defs = fieldDefs(ind.id);
    const v = {};
    Object.keys(defs).forEach((k) => {
      v[k] = sanitize(inputs && inputs[k] !== undefined ? inputs[k] : defs[k].default, defs[k]);
    });
    const ctx = { hourly: v.hourly, workdays: v.workdays };
    const lines = ind.drivers.map((d) => {
      if (!FORMULAS[d.type]) throw new Error('Unknown formula type: ' + d.type);
      const a = {};
      Object.keys(d.fields).forEach((k) => { a[k] = v[d.id + '.' + k]; });
      return { id: d.id, title: d.title, annual: FORMULAS[d.type](a, ctx) };
    });
    const totalCost = lines.reduce((s, l) => s + l.annual, 0);
    const savings = totalCost * v.improvementPct / 100;
    const addedLabelSpend = v.labelSpend * v.premiumPct / 100;
    const net = savings - addedLabelSpend;
    return {
      industryId: ind.id, lines, totalCost, improvementPct: v.improvementPct, savings,
      labelSpend: v.labelSpend, premiumPct: v.premiumPct, addedLabelSpend, net,
      multipleOfSpend: v.labelSpend > 0 ? totalCost / v.labelSpend : null,
      verdict: totalCost <= 0 ? 'empty' : (net > 0 ? 'pays' : 'doesnt')
    };
  }

  /** Display rounding: under 1,000 to the nearest 10, under 100,000 to the nearest 100, else the nearest 1,000. */
  function roundForDisplay(n) {
    if (!isFinite(n)) return 0;
    const abs = Math.abs(n);
    const step = abs < 1000 ? 10 : (abs < 100000 ? 100 : 1000);
    const r = Math.round(abs / step) * step;
    return n < 0 && r !== 0 ? -r : r;
  }

  let usd = null;
  /** Dollars after display rounding, no cents, e.g. "$12,300". */
  function formatUSD(n) {
    if (!usd) usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    return usd.format(roundForDisplay(n));
  }

  /** Share-link query string ("?i=...") holding only values that differ from the industry defaults. */
  function encodeState(industryId, inputs) {
    const ind = getIndustry(industryId);
    const defs = fieldDefs(ind.id);
    const p = new URLSearchParams();
    p.set('i', ind.id);
    Object.keys(defs).forEach((k) => {
      if (!inputs || inputs[k] === undefined) return;
      const n = sanitize(inputs[k], defs[k]);
      if (n !== defs[k].default) p.set(k, String(n));
    });
    return '?' + p.toString();
  }

  /** Query string back to { industryId (null if `i` invalid), inputs }; unknown keys and bad numbers are ignored, values clamped. */
  function decodeState(search, fallbackId) {
    const p = new URLSearchParams(search || '');
    const i = p.get('i');
    const valid = i && has(INDUSTRIES, i) ? i : null;
    const defs = fieldDefs(valid || fallbackId);
    const inputs = {};
    Object.keys(defs).forEach((k) => {
      const n = p.has(k) ? parseNumber(p.get(k)) : NaN;
      if (!isNaN(n)) inputs[k] = sanitize(n, defs[k]);
    });
    return { industryId: valid, inputs };
  }

  /** Every query key the calculator owns (`i` plus all input keys of all industries). */
  function stateKeys() {
    const keys = { i: true };
    Object.keys(INDUSTRIES).forEach((id) => Object.keys(fieldDefs(id)).forEach((k) => { keys[k] = true; }));
    return Object.keys(keys);
  }

  return {
    SOURCES, INDUSTRIES, SHARED_FIELDS, FORMULAS, FORMULA_WORDS,
    getIndustry, fieldDefs, defaultsFor, sourceIdsFor, parseNumber, sanitize, computeROI,
    roundForDisplay, formatUSD, encodeState, decodeState, stateKeys
  };
});
